import { FireTwoTone } from "@ant-design/icons";
import { Box } from "@chakra-ui/react";
import { Button, Col, Dropdown, Menu, Row, Space } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import { FilterValue } from "antd/lib/table/interface";
import { StaffRoutes } from "appRoutePaths";
import { AccountSearch, PackageTypeSearch } from "components/EntityTableSearch";
import MultiValueDisplay from "components/MultiValueDisplay";
import OwnersDisplay from "components/OwnersDisplay";
import BatchTable from "components/staff/Packages/BatchTable";
import EditPackageModal from "components/staff/Packages/EditPackageModal";
import FriendlyIdFilter from "components/staff/Packages/FriendlyIdFilter";
import StaffPackagesFilter, {
    StaffPackagesFilterDetail,
} from "components/staff/Packages/StaffPackagesFilter";
import StatusFilter from "components/staff/Packages/StatusFilter";
import TableActions from "components/TableActions";
import { Roles, useIdentity } from "context/auth";
import useStaffPackages, { useStaffPackagesCSV } from "hooks/useStaffPackages";
import { InitialPageRequest, PageRequest, PageSizeOptions } from "models/common";
import {
    PackageAmount,
    PackageStatus,
    StaffPackage
} from "models/packages";
import { useState } from "react";
import { Link } from "react-router-dom";
import downloadAsCSV from "util/downloadAsCSV";
import { localizedDate } from "util/helpers";
import { createCustomFilter, packageTypeDisplay } from "util/table";
import ClonePackageModal from "./ClonePackageModal";
import PackageDetailModal from "./PackageDetailModal";
import ReturnPackageModal from "./ReturnCardsModal";
import SendUpdateModal from "./SendUpdateModal";
import TrackingNumberFilter from "./TrackingNumberFilter";

const PackageActions = (props: {
    package: StaffPackage;
    onEdit: () => Promise<void>;
    allowSendUpdate: boolean;
    onCloneLink: (friendlyId: number) => void
}) => {
    const [editOpen, setEditOpen] = useState(false);
    const [returnOpen, setReturnOpen] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);
    const [cloneOpen, setCloneOpen] = useState(false);

    if (props.package.cloneFriendlyId) {
        return <div className="clone-cell">
            Cloned into
            <Button
                size="small"
                type="link"
                style={{ padding: "0 1px" }}
                onClick={() => props.onCloneLink(props.package.cloneFriendlyId!)}>
                #{props.package.cloneFriendlyId}
            </Button>
        </div>
    }

    const openQRCodeTab = () => {
        window.open(StaffRoutes.packageQRCode.url(props.package.id), '_blank');
    };

    return (
        <>
            <Dropdown.Button
                onClick={() => setEditOpen(true)}
                overlay={(() => (
                    <Menu items={[
                        {
                            key: "return",
                            label: "Return Card",
                            onClick: () => setReturnOpen(true)
                        },
                        {
                            key: "update",
                            label: "Send Update",
                            onClick: () => setUpdateOpen(true),
                            disabled: !props.allowSendUpdate
                        },
                        {
                            key: "qr",
                            label: "Print QR Code",
                            onClick: openQRCodeTab
                        },
                        { type: 'divider' },
                        {
                            key: "clone",
                            label: "Clone Package",
                            onClick: () => setCloneOpen(true)
                        }
                    ]} />
                ))()}
            >
                Edit
            </Dropdown.Button>
            <EditPackageModal
                open={editOpen}
                onClose={async (edited = false) => {
                    setEditOpen(false);
                    edited && await props.onEdit();
                }}
                package={props.package}
            />
            <ReturnPackageModal
                open={returnOpen}
                onClose={async (edited = false) => {
                    setReturnOpen(false);
                    edited && await props.onEdit();
                }}
                package={props.package}
            />
            <SendUpdateModal
                open={updateOpen}
                onClose={async (edited = false) => {
                    setUpdateOpen(false);
                    edited && await props.onEdit();
                }}
                package={props.package}
            />
            <ClonePackageModal
                open={cloneOpen}
                onClose={async (cloned = false) => {
                    setCloneOpen(false);
                    cloned && await props.onEdit();
                }}
                package={props.package}
            />
        </>
    );
};

const AllPackages = () => {
    const [{ page, filterBy, withinDays, sort, pageSize }, setPageRequest] =
        useState<PageRequest>(InitialPageRequest);
    const { isInRole } = useIdentity();

    const { isLoading, data, refetch } = useStaffPackages({
        page,
        filterBy,
        withinDays,
        sort,
        pageSize
    });
    const [filterDetail, setFilterDetail] = useState<StaffPackagesFilterDetail>(
        {}
    );
    const { data: records, totalRecords } = data || {};

    const daysChanged = (value: number) => {
        setPageRequest({ page: 1, filterBy, withinDays: value, sort, pageSize });
    };

    const tableChanged = (
        // pagination
        { current: newPage, pageSize: newPageSize }: any,
        // filtering
        filters: Record<string, FilterValue | null>,
        // sorting
        { column, field, order }: any
    ) => {
        setPageRequest({
            page: newPageSize !== pageSize ? 1 : newPage,
            pageSize: newPageSize,
            filterBy: filters,
            withinDays,
            sort: column ? [field, order === "ascend"] : null,
        });
    };

    const onFilterUpdate = (key: string) => (value: any) => {
        setFilterDetail((prev) => ({ ...prev, [key]: value }));
    };

    const columns: ColumnsType<StaffPackage> = [
        {
            title: "Status",
            dataIndex: "packageStatus",
            key: "packageStatus",
            sorter: false,
            ...createCustomFilter(
                StatusFilter,
                onFilterUpdate("status"),
                filterDetail.status
            ),
            render: (s) => PackageStatus[s],
        },
        {
            title: "ID",
            dataIndex: "friendlyId",
            key: "friendlyId",
            sorter: false,
            ...createCustomFilter(
                FriendlyIdFilter,
                onFilterUpdate("friendlyId"),
                filterDetail.friendlyId
            ),
            render: (id, rec) => <>
                <PackageDetailModal friendlyId={id} returnedCards={rec.returnedCards} />
                {rec.priority && <FireTwoTone twoToneColor={'#eb2f96'} style={{ marginLeft: '5px' }} title="Priority Processing" />}
            </>,
        },
        {
            title: "Trck.#",
            dataIndex: "trackingNumberSuffix",
            sorter: false,
            ...createCustomFilter(
                TrackingNumberFilter,
                onFilterUpdate("trackingNumberSuffix"),
                filterDetail.trackingNumberSuffix
            )
        },
        {
            title: "Sender",
            key: "sender",
            sorter: false,
            render: (_, rec) => <MultiValueDisplay title={rec.senderUserName} subTitle={rec.senderEmail} opacity="inherit" />
        },
        {
            title: "Customer",
            dataIndex: "accountId",
            key: "accountId",
            sorter: false,
            ...createCustomFilter(
                AccountSearch,
                onFilterUpdate("account"),
                filterDetail.account?.id
            ),
            render: (_, rec) => <OwnersDisplay owners={rec.owners} />,
        },
        {
            title: "Type",
            dataIndex: "packageType",
            key: "packageTypeId",
            sorter: false,
            ...createCustomFilter(
                PackageTypeSearch,
                onFilterUpdate("type"),
                filterDetail.type?.id
            ),
            render: (_, rec) => packageTypeDisplay(rec),
        },
        {
            title: "Amount",
            key: "batchCounts",
            sorter: false,
            align: "left",
            render: (_, rec) => rec.allowBatchAssignment
                ? <Space direction="vertical" size={2}>
                    {rec.scannableBatchCount > 0 && <span>{rec.scannableBatchCount} Scan</span>}
                    {rec.nonScannableBatchCount > 0 && <span>{rec.nonScannableBatchCount} Non-Scan</span>}
                </Space>
                : PackageAmount[rec.packageAmount]
        },
        {
            title: "Received Date",
            dataIndex: "receivedDate",
            key: "receivedDate",
            sorter: true,
            render: localizedDate,
        },
        {
            title: "Est. Upload Date",
            dataIndex: "estimatedUploadDate",
            key: "estimatedUploadDate",
            sorter: true,
            render: localizedDate,
        },
        {
            title: "Upload Date",
            dataIndex: "uploadDate",
            key: "uploadDate",
            sorter: true,
            render: localizedDate,
        },
        {
            title: "eBay Link",
            dataIndex: "ebayLink",
            key: "ebayLink",
            sorter: false,
            render: (link: string | null) =>
                link && (
                    <Button type="link" target="_blank" href={link}>
                        eBay Link
                    </Button>
                ),
        },
        {
            title: "",
            key: "edit",
            render: (_, rec) => (
                <PackageActions
                    package={rec}
                    onEdit={async () => {
                        await refetch();
                    }}
                    allowSendUpdate={isInRole([Roles.Admin, Roles.SeniorStaff])}
                    onCloneLink={(friendlyId: number) => {
                        tableChanged({ current: 1, pageSize }, { "friendlyId": [friendlyId] }, { column: null })
                        onFilterUpdate("friendlyId")(friendlyId);
                    }}
                />
            ),
        },
    ];

    const fetchCSV = useStaffPackagesCSV({
        filterBy,
        withinDays,
        sort,
    });

    const handleCSVExport = async () => {
        const csvData = await fetchCSV();
        const date = new Date().toISOString().split("T")[0];
        const filename = `all-packages-${date}.csv`;
        downloadAsCSV(csvData, filename);
    };

    return (
        <Box p={50}>
            <Row align="bottom">
                <Col span={6}>
                    <Box fontSize={36}>All Packages</Box>
                </Col>
                <Col span={10} offset={8} className="actions-container">
                    <TableActions
                        onDaysChanged={daysChanged}
                        isLoading={isLoading}
                        showFilter={false}
                        onExportCSV={handleCSVExport}
                    />
                </Col>
            </Row>
            <Row id="actions-and-filter">
                <Col span={3}>
                    <Button type="primary">
                        <Link to="/staff/packages/new">Add New Package</Link>
                    </Button>
                </Col>
                <Col span={3}>
                    <Button type="primary">
                        <Link to="/staff/packages/batch-assignment">Assign Batches</Link>
                    </Button>
                </Col>
                <Col span={18}>
                    <StaffPackagesFilter detail={filterDetail} />
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Table
                        id="staff-packages-table"
                        columns={columns}
                        dataSource={records}
                        rowKey={(r) => r.id}
                        rowClassName={(r) => r.cloneFriendlyId ? "read-only" : ""}
                        onChange={tableChanged}
                        pagination={{
                            total: totalRecords,
                            current: page,
                            pageSize,
                            pageSizeOptions: PageSizeOptions,
                            showSizeChanger: true,
                            showQuickJumper: true,
                        }}
                        expandable={{
                            rowExpandable: (r) => r.batches.length > 0,
                            expandedRowRender: (r) => (
                                <BatchTable
                                    batches={r.batches}
                                    onSuccess={async () => { await refetch(); }}
                                />
                            ),
                        }}
                        showSorterTooltip={false}
                        loading={isLoading}
                    />
                </Col>
            </Row>
        </Box>
    );
};

export default AllPackages;
